import React from 'react';
import { colors, typography, fontWeights, spacing } from '../../../styles/designTokens';

/**
 * @typedef {Object} KeyValueItem
 * @property {string} label                 Field label rendered above the value
 * @property {React.ReactNode} value         Field value — may be a string or a node
 */

/**
 * Replicates CloudScape's `KeyValuePairs` using designTokens. Renders a CSS grid
 * of `repeat(columns, 1fr)` where each item stacks a caption-styled label over a
 * body-styled value. Values may be plain strings or arbitrary React nodes (e.g. a
 * nested StatusIndicator). Collapses to a single column below 768px via `isMobile`
 * so the layout stays readable on small screens (Req 10.3).
 *
 * @param {Object} props
 * @param {number} props.columns                       Desktop column count for the grid
 * @param {Array<KeyValueItem>} props.items            Label/value pairs to render
 * @param {boolean} [props.isMobile]                   Collapses to one column when true
 */
export function KeyValuePairs({ columns, items, isMobile = false }) {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) {
    return null;
  }

  const requestedColumns = Math.max(1, Number(columns) || 1);
  const effectiveColumns = isMobile ? 1 : requestedColumns;

  return (
    <div
      data-testid="key-value-pairs"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${effectiveColumns}, 1fr)`,
        gap: spacing.sectionGap,
        width: '100%',
      }}
    >
      {list.map((item, index) => (
        <div
          key={item.label != null ? `${item.label}-${index}` : index}
          data-testid="key-value-pair"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            minWidth: 0,
          }}
        >
          <span
            data-testid="key-value-label"
            style={{
              fontSize: typography.caption.fontSize,
              fontWeight: typography.caption.fontWeight,
              color: colors.textSecondary,
            }}
          >
            {item.label}
          </span>
          <span
            data-testid="key-value-value"
            style={{
              fontSize: typography.body.fontSize,
              fontWeight: fontWeights.medium,
              color: colors.textPrimary,
              wordBreak: 'break-word',
            }}
          >
            {item.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export default KeyValuePairs;
